import { AnimatedSprite, Assets, Container, Rectangle, Texture } from 'pixi.js';

export type NpcState = 'idle' | 'walk' | 'run' | 'attack' | 'death';

export interface NpcConfig {
  name: string;
  basePath: string;
  prefix: string;
  animations: Record<NpcState, { file: string; frames: number }>;
  frameSize: number;
}

const NPC_CONFIGS: NpcConfig[] = [
  {
    name: 'Pink Monster',
    basePath: '/assets/sprites/npc/1 Pink_Monster',
    prefix: 'Pink_Monster',
    frameSize: 32,
    animations: {
      idle: { file: 'Pink_Monster_Idle_4.png', frames: 4 },
      walk: { file: 'Pink_Monster_Walk_6.png', frames: 6 },
      run: { file: 'Pink_Monster_Run_6.png', frames: 6 },
      attack: { file: 'Pink_Monster_Attack1_4.png', frames: 4 },
      death: { file: 'Pink_Monster_Death_8.png', frames: 8 },
    },
  },
  {
    name: 'Owlet Monster',
    basePath: '/assets/sprites/npc/2 Owlet_Monster',
    prefix: 'Owlet_Monster',
    frameSize: 32,
    animations: {
      idle: { file: 'Owlet_Monster_Idle_4.png', frames: 4 },
      walk: { file: 'Owlet_Monster_Walk_6.png', frames: 6 },
      run: { file: 'Owlet_Monster_Run_6.png', frames: 6 },
      attack: { file: 'Owlet_Monster_Attack1_4.png', frames: 4 },
      death: { file: 'Owlet_Monster_Death_8.png', frames: 8 },
    },
  },
  {
    name: 'Dude Monster',
    basePath: '/assets/sprites/npc/3 Dude_Monster',
    prefix: 'Dude_Monster',
    frameSize: 32,
    animations: {
      idle: { file: 'Dude_Monster_Idle_4.png', frames: 4 },
      walk: { file: 'Dude_Monster_Walk_6.png', frames: 6 },
      run: { file: 'Dude_Monster_Run_6.png', frames: 6 },
      attack: { file: 'Dude_Monster_Attack1_4.png', frames: 4 },
      death: { file: 'Dude_Monster_Death_8.png', frames: 8 },
    },
  },
];

export const getNpcConfigs = (): NpcConfig[] => NPC_CONFIGS;

export class Npc {
  readonly container: Container;
  private sprites: Map<NpcState, AnimatedSprite> = new Map();
  private currentState: NpcState = 'idle';
  private currentSprite: AnimatedSprite | null = null;
  private _alive = true;
  private _direction = 1;
  private _vx = 0;
  readonly config: NpcConfig;

  constructor(config: NpcConfig) {
    this.config = config;
    this.container = new Container();
  }

  async loadAnimations(): Promise<void> {
    const { basePath, animations, frameSize } = this.config;

    for (const [state, anim] of Object.entries(animations)) {
      const url = `${basePath}/${anim.file}`;
      const texture = await Assets.load<Texture>(url);
      const baseTexture = texture.source;
      baseTexture.scaleMode = 'nearest';

      const frames: Texture[] = [];
      for (let i = 0; i < anim.frames; i++) {
        const frame = new Texture({
          source: baseTexture,
          frame: new Rectangle(i * frameSize, 0, frameSize, frameSize),
        });
        frames.push(frame);
      }

      const sprite = new AnimatedSprite(frames);
      sprite.anchor.set(0.5, 1);
      sprite.animationSpeed = 0.15;
      sprite.visible = false;
      sprite.scale.set(2);
      this.container.addChild(sprite);
      this.sprites.set(state as NpcState, sprite);
    }

    this.setState('idle');
  }

  get alive(): boolean {
    return this._alive;
  }

  get direction(): number {
    return this._direction;
  }

  set direction(value: number) {
    this._direction = value;
    this.container.scale.x = value < 0 ? -1 : 1;
  }

  get vx(): number {
    return this._vx;
  }

  set vx(value: number) {
    this._vx = value;
    if (value !== 0) {
      this.direction = value > 0 ? 1 : -1;
    }
  }

  get state(): NpcState {
    return this.currentState;
  }

  setState(state: NpcState): void {
    if (this.currentState === state && this.currentSprite) return;
    if (!this._alive && state !== 'death') return;

    if (this.currentSprite) {
      this.currentSprite.stop();
      this.currentSprite.visible = false;
    }

    this.currentState = state;
    const sprite = this.sprites.get(state);
    if (!sprite) return;

    this.currentSprite = sprite;
    sprite.visible = true;
    sprite.gotoAndPlay(0);

    if (state === 'death') {
      sprite.loop = false;
      sprite.onComplete = () => {
        this._alive = false;
      };
    } else if (state === 'attack') {
      sprite.loop = false;
      sprite.onComplete = () => {
        if (this._alive) {
          this.setState('idle');
        }
      };
    } else {
      sprite.loop = true;
      sprite.onComplete = undefined;
    }
  }

  die(): void {
    this.vx = 0;
    this.setState('death');
  }

  reset(x: number, y: number): void {
    this._alive = true;
    this.container.position.set(x, y);
    this.vx = 0;
    this.setState('idle');
    this.container.visible = true;
    this.container.alpha = 1;
  }

  hide(): void {
    this.container.visible = false;
  }
}
